/**
 * Runs an allow-listed HTML frame action through the app's own entry
 * points. Scope comes from the host frame, never from the page: items open
 * in the frame's project, a question is answered only when it is pending
 * on this same frame with an option it offers.
 */
import { replyAwaitingInput } from "@/api/awaiting-input";
import {
  openFrameInProject,
  openTaskInProject,
} from "@/components/layout/cross-project-navigation";
import { changeProjectTaskStatus } from "@/components/panels/task-actions";
import { normalizeToolkitSlug } from "@/lib/composio";
import {
  useCanvasStore,
  type AwaitingInput,
  type AwaitingInputOption,
} from "@/stores/canvas-store";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import type {
  HtmlFrameActionHost,
  HtmlFrameHostAction,
} from "./html-frame-actions";

/** The pending question on `frameId` and its option, if both exist. */
export function pendingHtmlFrameQuestion(
  frameId: string,
  questionId: string,
  optionId: string,
): { question: AwaitingInput; option: AwaitingInputOption } | null {
  const question = useCanvasStore
    .getState()
    .awaitingInputs.get(frameId)
    ?.find((item) => item.id === questionId);
  const option = question?.options.find((item) => item.id === optionId);
  return question && option ? { question, option } : null;
}

export async function runHtmlFrameHostAction(
  host: HtmlFrameActionHost,
  action: HtmlFrameHostAction,
): Promise<void> {
  const { projectId } = host;
  switch (action.name) {
    case "open_connect":
      useDesktopMode.getState().requestSettings({
        page: "connections-app",
        args: { app: normalizeToolkitSlug(action.args.toolkit) },
      });
      return;
    case "open_frame":
      await openFrameInProject({
        id: action.args.frameId,
        projectId,
        projectName: null,
      });
      return;
    case "open_task":
      await openTaskInProject({
        id: action.args.taskId,
        projectId,
        projectName: null,
      });
      return;
    case "answer_question": {
      const pending = pendingHtmlFrameQuestion(
        host.frameId,
        action.args.questionId,
        action.args.optionId,
      );
      if (!pending) return;
      await replyAwaitingInput(projectId, pending.question.id, {
        answer: pending.option.label,
        selectedOption: pending.option.id,
      });
      return;
    }
    case "run_task":
      await changeProjectTaskStatus(
        projectId,
        action.args.taskId,
        "in_progress",
      );
      return;
  }
}
